// ─────────────────────────────────────────────────────────────────────────────
// destinationChanges.js
//
// Which platforms have edits that are not saved yet, by comparing the editor's
// current state with what the server last returned (see fetchKeys in
// Home/index.jsx). Drives the "Unsaved changes" bar, the dot beside each
// platform in the list, and the "discard changes?" check on close.
//
// Before this the editor had no idea anything was unsaved: closing the dialog
// kept the typed values in memory, so they reappeared next time looking saved,
// and a sign-in redirect for YouTube/Facebook dropped them silently.
// ─────────────────────────────────────────────────────────────────────────────

export const URL_PLATFORMS = ["linkedin", "x", "instagram", "kick", "rtmp"];

export const PLATFORM_LABELS = {
  youtube: "YouTube", facebook: "Facebook", tiktok: "TikTok", linkedin: "LinkedIn",
  x: "X", instagram: "Instagram", kick: "Kick", rtmp: "Custom RTMP"
};

const str = (v) => (typeof v === "string" ? v : "");

const rowsOf = (rows, platform) =>
  (rows || [])
    .filter((r) => (r?.platform || "") === (platform === "rtmp" ? "" : platform))
    .map((r) => [str(r.url), str(r.key), str(r.name)]);

const sameList = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const labelsDiffer = (cur, saved, ids) =>
  ids.some((id) => str(cur?.[id]).trim() !== str(saved?.[id]).trim());

/**
 * @param current { youtubeKeys, facebookKeys, tiktokKey, rtmpDestinations, labels }
 * @param saved   same shape, or null before the first load (→ nothing unsaved)
 * @returns platform ids with unsaved edits, in list order
 */
export function unsavedPlatforms(current, saved) {
  if (!saved) return [];
  const out = [];
  const slotIds = (prefix) => [1, 2, 3].map((n) => `${prefix}${n}`);

  if (!sameList(current.youtubeKeys, saved.youtubeKeys) ||
      labelsDiffer(current.labels, saved.labels, slotIds("YouTube"))) out.push("youtube");
  if (!sameList(current.facebookKeys, saved.facebookKeys) ||
      labelsDiffer(current.labels, saved.labels, slotIds("Facebook"))) out.push("facebook");
  if (str(current.tiktokKey) !== str(saved.tiktokKey) ||
      labelsDiffer(current.labels, saved.labels, ["TikTok"])) out.push("tiktok");

  URL_PLATFORMS.forEach((p) => {
    if (!sameList(rowsOf(current.rtmpDestinations, p), rowsOf(saved.rtmpDestinations, p))) out.push(p);
  });
  return out;
}

/** "YouTube", "YouTube and Kick", "YouTube, Kick and X" */
export function listPlatformNames(ids) {
  const names = ids.map((id) => PLATFORM_LABELS[id] || id);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
